import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { enterpriseApiClient } from '@api/enterprise/client';
import { WorkOrdersApi, OrderDetailStatus } from '@api/enterprise/openapi';
import type {
  WorkOrderDetailViewModel,
  SkipWorkOrderDetailCommand
} from '@api/enterprise/openapi';
import { workOrderKeys, WORK_ORDERS_QUERY_KEY } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';

type SkipWorkOrderDetailParams = {
  jobNumber: string;
  sequenceNumber: number;
  command?: SkipWorkOrderDetailCommand;
};

export const useSkipWorkOrderDetail = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    WorkOrderDetailViewModel,
    Error,
    SkipWorkOrderDetailParams
  >({
    mutationFn: async ({
      jobNumber,
      sequenceNumber,
      command
    }: SkipWorkOrderDetailParams) => {
      if (!navigator.onLine) {
        await db.transaction(
          'rw',
          [db.cachedWorkOrderDetails, db.syncQueue],
          async () => {
            const cached = await db.cachedWorkOrderDetails
              .where('[jobNumber+sequenceNumber]')
              .equals([jobNumber, sequenceNumber])
              .first();

            if (cached) {
              await db.cachedWorkOrderDetails
                .where('[jobNumber+sequenceNumber]')
                .equals([jobNumber, sequenceNumber])
                .modify({
                  status: OrderDetailStatus.Skipped
                });
            }

            await enqueue({
              entityType: 'wo_detail_skip',
              entityKey: `${jobNumber}-${sequenceNumber}`,
              payload: {
                jobNumber,
                sequenceNumber,
                command,
                originalState: cached?.status
              },
              priority: 40
            });
          }
        );

        return {
          jobNumber,
          sequenceNumber,
          status: OrderDetailStatus.Skipped
        } as WorkOrderDetailViewModel;
      }

      const api = new WorkOrdersApi(undefined, undefined, enterpriseApiClient);
      const response =
        await api.apiWorkOrdersJobNumberDetailsSequenceNumberSkipPost({
          jobNumber,
          sequenceNumber,
          skipWorkOrderDetailCommand: command
        });
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: [WORK_ORDERS_QUERY_KEY, 'details', variables.jobNumber]
      });
      queryClient.invalidateQueries({
        queryKey: ['workOrderDetails', variables.jobNumber]
      });

      showSuccess('detail.skip.success', 'workOrders');
      onSuccessCallback?.();

      if (!navigator.onLine) {
        processAll();
      }

      if (navigator.onLine) {
        try {
          const metadata = await db.downloadMetadata.get(
            String(variables.jobNumber)
          );
          if (metadata?.status === 'cached') {
            await db.cachedWorkOrderDetails
              .where('[jobNumber+sequenceNumber]')
              .equals([String(variables.jobNumber), variables.sequenceNumber])
              .modify({
                status: OrderDetailStatus.Skipped
              });
          }
        } catch {
          // Best effort
        }
      }
    },
    onError: (error, _variables) => {
      showError('detail.skip.error', error, 'workOrders');
    }
  });
};
